/* Rebuy owns bundle pricing, attribution and cart submission. This controller owns only this alternate PDP. */
(() => {
  const kid = (p) => /\b(girls?|kids?|toddler|baby)\b/i.test(`${p.title} ${p.handle}`);
  const pairFrom = (products, inputId) => {
    const unique = [...new Map(products.map(p => [String(p.id), p])).values()];
    if (unique.length !== 2 || !unique.some(p => String(p.id) === String(inputId)) || unique.filter(kid).length !== 1) return null;
    return unique.sort((a, b) => Number(kid(a)) - Number(kid(b)));
  };
  const variantWithOptions = (product, values) => product.variants.find(v =>
    values.every((value, index) => String(v[`option${index + 1}`]) === String(value)));
  const selectionIssues = (slots, available) => (slots || []).flatMap((slot, slotIndex) => {
    const missing = slot.product.options.flatMap((option, optionIndex) => slot.values[optionIndex] ? [] : [{ slotIndex, optionIndex, name: option.name, missing: true }]);
    if (missing.length) return missing;
    if (slot.chosen && slot.product.selected_variant && available(slot.product.selected_variant)) return [];
    const sizeIndex = slot.product.options.findIndex(option => /size/i.test(option.name));
    const optionIndex = sizeIndex < 0 ? 0 : sizeIndex;
    return [{ slotIndex, optionIndex, name: slot.product.options[optionIndex].name, missing: false }];
  });
  const uniqueFamily = (items) => [...new Map(items.filter(p => p.id && /^[a-z0-9-]+$/i.test(p.handle)).map(p => [String(p.id), p])).values()];
  const quantity = (cart, id) => cart.items.filter(i => String(i.variant_id) === String(id)).reduce((n, i) => n + i.quantity, 0);
  const cartContainsAddition = (before, after, ids) => ids.every(id => quantity(after, id) >= quantity(before, id) + 1);
  const addWithoutRedirect = widget => {
    // Scope the override to this alternate PDP's widget instance. Rebuy's native
    // submission still supplies bundle properties/discount attribution.
    widget.data.config = { ...widget.data.config, redirect: 'none' };
    return widget.addSelectedProductsToCart();
  };
  const openBundleCart = (target, opener) => {
    target.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    target.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener } }));
  };
  const formatSubtotal = (cents, currency, locale = 'en-US') => new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents) / 100);
  const imageSource = image => typeof image === 'string' ? image : image?.src || '';
  const colorKey = p => (p.color || p.label || '').toLowerCase().replace(/\bmetallic\b/g, '').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();
  const eligibleFamily = (product, family) => {
    // These are the approved launch colors, not new Rebuy pairing rules.
    // Match current catalog color metadata, not historical product URL names.
    const allowed = product.handle.includes('laurin') ? ['platinum', 'white', 'rose quartz']
      : product.handle.includes('jacks-eva-') ? ['pale pink', 'cream', 'fuchsia', 'navy', 'bright yellow', 'lilac'] : null;
    return allowed ? family.filter(p => allowed.includes(colorKey(p))) : family;
  };
  const sharedColors = slots => {
    if (slots.length !== 2) return [];
    const order = ['rose quartz', 'platinum', 'navy', 'silver', 'white', 'cork & gold'];
    return slots[0].family.filter(p => p.available !== false).map(adult => ({ adult, child: slots[1].family.find(child => colorKey(child) === colorKey(adult) && child.available !== false) }))
      .filter(p => p.child && colorKey(p.adult)).sort((a, b) => {
        const rank = p => order.includes(colorKey(p)) ? order.indexOf(colorKey(p)) : order.length;
        return rank(a.adult) - rank(b.adult);
      });
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { pairFrom, variantWithOptions, selectionIssues, uniqueFamily, eligibleFamily, cartContainsAddition, formatSubtotal, imageSource, colorKey, sharedColors, addWithoutRedirect, openBundleCart };
  if (typeof HTMLElement === 'undefined' || customElements.get('mommy-me-bundle')) return;

  const element = (tag, text, attrs = {}) => {
    const node = document.createElement(tag);
    if (text != null) node.textContent = text;
    for (const [name, value] of Object.entries(attrs)) node.setAttribute(name, value);
    return node;
  };
  const request = async (url, signal) => {
    const response = await fetch(url, { signal, credentials: new URL(url, location.origin).origin === location.origin ? 'same-origin' : 'omit' });
    if (!response.ok) throw new Error('Could not load the latest bundle information. Please reload and try again.');
    return response;
  };

  class MommyMeBundle extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.config = JSON.parse(this.querySelector('[data-bundle-config]').textContent);
      this.requestedColor = new URLSearchParams(location.search).get('bundle_color');
      this.status = this.querySelector('[data-status]');
      this.addButton = this.querySelector('[data-add]');
      this.familyCache = new Map();
      this.guideCache = new Map();
      this.productCache = new Map();
      // Shopify can supply this member's family before the Rebuy SDK or pairing request completes.
      try {
        const initial = this.querySelector('[data-mm-product-family]');
        if (initial) this.readFamily({ id: this.config.productId, handle: JSON.parse(initial.textContent).handle }, this);
      } catch { /* The read-only endpoint remains a fallback for malformed inline data. */ }
      this.hydrateEditorialGallery();
      this.addButton.addEventListener('click', () => this.add(), { signal: this.abort.signal });
      this.addEventListener('change', event => this.change(event), { signal: this.abort.signal });
      this.addEventListener('click', event => {
        const sizeGuide = event.target.closest('button[data-guide-id]');
        if (sizeGuide) document.getElementById(sizeGuide.dataset.guideId)?.open(sizeGuide);
        const swatch = event.target.closest('button[data-shared-color]');
        if (swatch && !this.busy && !this.uncertain) {
          const pair = sharedColors(this.slots).find(p => colorKey(p.adult) === swatch.dataset.sharedColor);
          if (pair) this.changeColors([pair.adult, pair.child]);
        }
      }, { signal: this.abort.signal });
      document.addEventListener('rebuy.ready', event => this.accept(event.detail?.widget), { signal: this.abort.signal });
      this.started = Date.now();
      const checkWidget = () => {
        const widget = window.Rebuy?.widgets?.find(w => String(w.id) === this.dataset.widgetId);
        if (widget?.data?.products?.length) this.accept(widget);
        if (Date.now() - this.started > 20000 && !this.widget) {
          clearInterval(this.poll);
          this.failLoading('This bundle is unavailable right now. Please reload or shop the products individually.');
        }
      };
      this.poll = setInterval(checkWidget, 250);
      checkWidget();
    }

    disconnectedCallback() {
      clearInterval(this.poll);
      this.abort?.abort();
    }

    message(text) { this.status.textContent = text; }

    failLoading(text) {
      this.removeAttribute('data-loading');
      this.querySelector('[data-selectors]').setAttribute('aria-busy', 'false');
      this.querySelectorAll('[data-shared-wrap]').forEach(node => { node.hidden = true; });
      this.querySelectorAll('[data-subtitle], [data-price], [data-selectors]').forEach(node => node.replaceChildren());
      this.addButton.disabled = true;
      this.addButton.textContent = 'Bundle unavailable';
      this.message(text);
    }

    hydrateEditorialGallery() {
      const stage = this.querySelector('[data-editorial-stage] img');
      if (!stage) return;
      const thumbs = [...this.querySelectorAll('[data-editorial-index]')];
      const images = this.config.gallery?.length ? this.config.gallery : [{ src: this.config.image, alt: 'Mommy & Me bundle' }];
      const show = index => {
        if (!images[index]?.src) return;
        stage.removeAttribute('srcset');
        stage.src = images[index].src;
        stage.alt = images[index].alt;
        thumbs.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      };
      thumbs.forEach((button, i) => button.addEventListener('click', () => show(i), { signal: this.abort.signal }));
      this.querySelectorAll('[data-editorial-arrow]').forEach(button => button.addEventListener('click', () => {
        const current = thumbs.findIndex(thumb => thumb.getAttribute('aria-pressed') === 'true');
        const index = (current + Number(button.dataset.editorialArrow) + thumbs.length) % thumbs.length;
        show(index);
        const strip = thumbs[index]?.parentElement;
        if (strip) strip.scrollTo({ left: thumbs[index].offsetLeft - strip.offsetLeft, behavior: 'auto' });
      }, { signal: this.abort.signal }));
    }

    async accept(widget) {
      if (!this.isConnected || this.widget || String(widget?.id) !== this.dataset.widgetId || !widget.data?.products?.length) return;
      clearInterval(this.poll);
      const pair = pairFrom(widget.data.products, this.config.productId);
      if (!pair || typeof widget.addSelectedProductsToCart !== 'function') {
        this.failLoading('No complete Mommy & Me pairing is available for this product.');
        return;
      }
      this.widget = widget;
      this.initialIds = pair.map(p => String(p.id));
      this.slots = pair.map(product => ({ product, chosen: false, family: this.familyCache.get(product.handle) || [], guideId: this.guideCache.get(product.handle), values: product.options.map(option => option.values.length === 1 ? String(option.values[0]) : '') }));
      this.familiesLoading = true;
      // Start the missing member's request before the first render. The inline member is a cache hit.
      const families = this.slots.map(slot => this.family(slot.product).catch(() => [{ id: slot.product.id, handle: slot.product.handle, label: slot.product.title }]));
      this.sync();
      try { this.render(); }
      catch { this.failLoading('This bundle could not load. Please reload the page.'); return; }
      this.message('Select the options for both pairs.');
      await Promise.all(this.slots.map(async (slot, index) => {
        slot.family = await families[index];
        slot.guideId = this.guideCache.get(slot.product.handle);
      }));
      this.familiesLoading = false;
      if (this.isConnected) {
        this.render();
        if (this.requestedColor) await this.selectRequestedColor();
      }
    }

    async selectRequestedColor() {
      const key = colorKey({ color: this.requestedColor.replace(/-/g, ' ') });
      const pair = this.config.allowColorChanges && this.config.publicKey
        ? sharedColors(this.slots).find(pair => colorKey(pair.adult) === key) : null;
      if (!pair) {
        this.failLoading('The requested bundle color is unavailable. Please return to the Mommy & Me page and choose another color.');
        return;
      }
      await this.changeColors([pair.adult, pair.child]);
      if (![pair.adult, pair.child].every((product, index) => String(product.id) === String(this.slots[index].product.id))) {
        this.failLoading('The requested bundle color could not load. Please reload before choosing your sizes.');
        return;
      }
      this.requestedColorApplied = true;
      this.render();
    }

    async family(product) {
      if (this.familyCache.has(product.handle)) return this.familyCache.get(product.handle);
      if (!/^[a-z0-9-]+$/i.test(product.handle)) throw new Error('Invalid product.');
      const url = new URL(`${this.config.root}products/${product.handle}`, location.origin);
      url.searchParams.set('section_id', 'mommy-and-me-product-data');
      const html = await (await request(url, this.abort.signal)).text();
      const documentData = new DOMParser().parseFromString(html, 'text/html');
      return this.readFamily(product, documentData);
    }

    readFamily(product, documentData) {
      const data = documentData.querySelector('[data-mm-product-family]');
      if (!data) throw new Error('Color family unavailable.');
      const parsed = JSON.parse(data.textContent);
      if (String(parsed.id) !== String(product.id) || (parsed.handle && parsed.handle !== product.handle)) throw new Error('Product mismatch.');
      const guide = documentData.querySelector('[data-mm-size-guide]');
      if (guide) {
        this.guideCache.set(product.handle, guide.id);
        if (documentData !== this && !this.querySelector(`[id="${guide.id}"]`)) this.append(document.importNode(guide, true));
      }
      const family = eligibleFamily(product, uniqueFamily(parsed.products));
      // Labels rendered by the shared Liquid snippet have HTML entities, not HTML content.
      family.forEach(p => { const decoded = document.createElement('textarea'); decoded.innerHTML = p.label; p.label = decoded.value; });
      this.familyCache.set(product.handle, family);
      return family;
    }

    async colorProduct(candidate) {
      if (this.productCache.has(candidate.id)) return this.productCache.get(candidate.id);
      const url = new URL('https://rebuyengine.com/api/v1/products/static');
      url.search = new URLSearchParams({ key: this.config.publicKey, ids: candidate.id, filter_oos: 'no', filter_unpublished: 'yes', country_code: this.config.country, limit: '1' });
      const json = await (await request(url, this.abort.signal)).json();
      const product = json.data?.find(p => String(p.id) === String(candidate.id));
      if (!product?.variants?.length || product.handle !== candidate.handle) throw new Error('This color is not available in your market.');
      this.productCache.set(candidate.id, product);
      return product;
    }

    sync() {
      // Do not alter the admin settings or refetch the matched rule when one color changes:
      // that would reset the other shopper selection to the rule's preset color.
      for (const slot of this.slots) { slot.product.selected = true; slot.product.quantity = 1; }
      this.widget.data.products = this.slots.map(slot => slot.product);
    }

    ready() {
      return !this.busy && !this.uncertain && this.slots?.length === 2 && this.slots.every(slot =>
        slot.chosen && slot.product.selected_variant && this.widget.variantAvailable(slot.product.selected_variant));
    }

    priceText(value) {
      // Rebuy subtotal/compareAtSubtotal return cents; variantPrice returns major units.
      return formatSubtotal(value, this.config.currency, document.documentElement.lang || 'en');
    }

    paintSwatch(node, product) {
      node.dataset.swatch = product.color || colorKey(product);
      if (product.swatchImage) node.style.setProperty('--swatch-background-image', `url("${product.swatchImage}")`);
      if (product.swatchColor) node.style.setProperty('--swatch-background-color', `rgb(${product.swatchColor})`);
    }

    renderSharedColors() {
      const pairs = this.config.allowColorChanges && this.config.publicKey ? sharedColors(this.slots) : [];
      if (this.familiesLoading && this.config.allowColorChanges && this.config.publicKey && !pairs.length) return;
      this.querySelectorAll('[data-shared-wrap]').forEach(n => { n.hidden = !pairs.length; });
      this.querySelectorAll('[data-shared-swatches]').forEach(row => {
        row.replaceChildren();
        pairs.forEach(pair => {
          const selected = [pair.adult, pair.child].every((p, i) => String(p.id) === String(this.slots[i].product.id));
          const key = colorKey(pair.adult);
          const button = element('button', null, { type: 'button', class: 'mm-bundle__swatch', 'data-shared-color': key, 'data-focus-key': `shared-${row.dataset.swatchContext}-${key}`, 'aria-pressed': selected, 'aria-label': `${pair.adult.label} for both shoes` });
          button.disabled = !!this.busy || !!this.uncertain;
          const dot = element('span', null, { class: 'mm-bundle__swatch-dot', 'aria-hidden': 'true' });
          this.paintSwatch(dot, pair.adult);
          button.append(dot, element('span', pair.adult.label.replace(/^Metallic /, '')));
          row.append(button);
        });
      });
      const matched = pairs.some(pair => [pair.adult, pair.child].every((p, i) => String(p.id) === String(this.slots[i].product.id)));
      this.querySelector('[data-color-note]').textContent = matched ? 'Matching colors selected. You can change either shoe below.' : 'Mix & match: choose colors individually, or select a matching color above.';
    }

    render() {
      // Do not expose the trigger color's purchase form while applying a deep link.
      if (!this.slots || (this.requestedColor && !this.requestedColorApplied)) return;
      const issues = selectionIssues(this.slots, variant => this.widget.variantAvailable(variant));
      const activeKey = document.activeElement?.dataset?.focusKey;
      const selectors = this.querySelector('[data-selectors]');
      selectors.replaceChildren();
      this.slots.forEach((slot, index) => {
        const group = element('fieldset');
        group.disabled = !!this.busy || !!this.uncertain;
        group.append(element('legend', index === 0 ? "Women's sandals" : "Girls' sandals"));
        if (this.config.sizeGuide) group.append(element('a', 'Size guide', { class: 'mm-bundle__size-link', href: this.config.sizeGuide }));
        else if (slot.guideId) group.append(element('button', 'Size guide', { type: 'button', class: 'mm-bundle__size-link', 'data-guide-id': slot.guideId, 'aria-haspopup': 'dialog' }));
        const current = slot.family.find(p => String(p.id) === String(slot.product.id));
        const color = element('p', `Selected color: ${current?.label || slot.product.options.find(o => /colou?r/i.test(o.name))?.values[0] || ''}`, { class: 'mm-bundle__selected-color' });
        if (current) { const dot = element('span', null, { class: 'mm-bundle__swatch-dot', 'aria-hidden': 'true' }); this.paintSwatch(dot, current); color.append(dot); }
        group.append(color);
        let colorSelect;
        if (slot.family.length > 1 && this.config.allowColorChanges && this.config.publicKey) {
          const id = `${this.id || this.dataset.widgetId}-color-${index}`;
          colorSelect = element('div');
          colorSelect.append(element('label', 'Select color:', { for: id }));
          const select = element('select', null, { id, 'data-slot': index, 'data-color': '', 'data-focus-key': id });
          slot.family.forEach(p => {
            const option = element('option', p.label || p.handle, { value: p.id });
            option.selected = String(p.id) === String(slot.product.id);
            option.disabled = p.available === false;
            select.append(option);
          });
          colorSelect.append(select);
        }
        slot.product.options.forEach((option, optionIndex) => {
          // A fixed native color is redundant when the product-family selector is available.
          if (/colou?r/i.test(option.name) && option.values.length === 1 && slot.family.length > 1 && this.config.publicKey && this.config.allowColorChanges) return;
          const id = `${this.dataset.widgetId}-option-${index}-${optionIndex}`;
          const issue = this.validationAttempted && issues.find(issue => issue.slotIndex === index && issue.optionIndex === optionIndex);
          group.append(element('label', issue ? `Choose ${issue.missing ? 'a' : 'an available'} ${option.name}` : `Select ${option.name.toLowerCase()}:`, { for: id, class: issue ? 'mm-bundle__option-error' : '' }));
          const select = element('select', null, { id, 'data-slot': index, 'data-option': optionIndex, 'data-focus-key': id });
          if (issue) select.setAttribute('aria-invalid', 'true');
          select.append(element('option', `Select ${option.name.toLowerCase()}`, { value: '' }));
          option.values.forEach(value => {
            const available = slot.product.variants.some(v => String(v[`option${optionIndex + 1}`]) === String(value) && this.widget.variantAvailable(v));
            const item = element('option', `${value}${available ? '' : ' — Sold out'}`, { value });
            item.disabled = !available;
            item.selected = slot.values[optionIndex] === String(value);
            select.append(item);
          });
          group.append(select);
        });
        if (colorSelect) group.append(colorSelect);
        selectors.append(group);
      });
      this.renderSharedColors();
      const sizeHelp = this.querySelector('[data-size-help]');
      if (sizeHelp) {
        sizeHelp.hidden = !this.slots.some(s => s.guideId);
        this.querySelector('[data-size-links]').replaceChildren(...this.slots.flatMap((slot, i) => slot.guideId ? [element('button', i === 0 ? "Women's size guide" : "Girls' size guide", { type: 'button', class: 'mm-bundle__size-link', 'data-guide-id': slot.guideId, 'aria-haspopup': 'dialog' })] : []));
      }
      this.querySelector('[data-subtitle]').textContent = this.slots.map(s => s.product.title).join(' + ');
      const price = this.querySelector('[data-price]');
      const regularSubtotal = Number(this.widget.subtotal());
      const estimate = window.Jru450BundlePromotion?.quote(this.config.evaPromotion, this.slots.map(slot => ({
        productId: slot.product.id,
        priceCents: Math.round(Number(slot.product.selected_variant?.price) * 100),
        sellingPlan: slot.product.selling_plan_id
      })));
      // Do not replace an independently discounted Rebuy offer or mutate its underlying prices.
      const promotion = estimate && estimate.regularCents === regularSubtotal ? estimate : null;
      this.evaPromotion = promotion;
      const displaySubtotal = promotion ? promotion.totalCents : regularSubtotal;
      price.replaceChildren(element('span', this.priceText(displaySubtotal)));
      if (promotion) price.append(element('del', this.priceText(regularSubtotal)));
      else if (this.widget.bundleOnSale()) price.append(element('del', this.priceText(this.widget.compareAtSubtotal())));
      const promotionNote = this.querySelector('[data-eva-promotion]');
      if (promotionNote) promotionNote.hidden = !promotion;
      this.querySelector('[data-included]').replaceChildren(...this.slots.map(s => element('li', `1 × ${s.product.title}`)));
      this.addButton.disabled = !!this.busy || !!this.uncertain;
      this.addButton.classList.toggle('mm-bundle__submit--pick', issues.length > 0);
      this.addButton.textContent = this.busy ? 'Please wait…' : this.uncertain ? 'Check your bag' : issues.length
        ? (issues.some(issue => /size/i.test(issue.name)) ? 'Pick sizes' : 'Pick options')
        : `Add bundle to bag | ${this.priceText(displaySubtotal)}${promotion ? ' with EVA offer' : ''}`;
      this.gallery();
      const promotionBadge = this.querySelector('[data-eva-badge]');
      if (promotionBadge) promotionBadge.hidden = !promotion;
      this.querySelectorAll('.mm-bundle__badge').forEach(badge => { badge.hidden = !!promotion; });
      this.removeAttribute('data-loading');
      selectors.setAttribute('aria-busy', 'false');
      if (activeKey) [...this.querySelectorAll('[data-focus-key]')].find(n => n.dataset.focusKey === activeKey)?.focus();
    }

    gallery() {
      const gallery = this.querySelector('[data-gallery]');
      // Keep the already-loaded Liquid image and the shopper's selected thumbnail during hydration.
      if (!this.colorsChanged && gallery.querySelector('[data-editorial-stage]')) return;
      const editorial = !this.colorsChanged && this.config.gallery?.length ? this.config.gallery.filter(i => i.src) : [];
      const images = editorial.length ? editorial : this.slots.flatMap(s => (s.product.images || []).map(i => ({ src: i.src, alt: s.product.title }))).filter(i => i.src).slice(0, 10);
      const sources = this.slots.map(s => ({ src: imageSource((s.product.images || []).find(i => String(i.id) === String(s.product.selected_variant?.image_id))) || imageSource(s.product.image) || imageSource(s.product.images?.[0]), alt: s.product.title }));
      const signature = JSON.stringify([sources, editorial, this.colorsChanged]);
      if (signature === this.gallerySignature) return;
      this.gallerySignature = signature;
      gallery.replaceChildren();
      const stage = element('div', null, { class: 'mm-bundle__stage' });
      const showPair = () => {
        stage.replaceChildren();
        // Combined editorial photography only represents the initial pair, never mixed colors.
        if (editorial.length) stage.append(element('img', null, { class: 'mm-bundle__photo', src: editorial[0].src, alt: editorial[0].alt, width: 1000, height: 1000 }));
        else if (this.config.image && !this.colorsChanged) stage.append(element('img', null, { class: 'mm-bundle__photo', src: this.config.image, alt: 'Mommy & Me bundle', width: 1000, height: 1000 }));
        else {
          const pair = element('div', null, { class: 'mm-bundle__pair' });
          sources.filter(i => i.src).forEach(i => pair.append(element('img', null, { src: i.src, alt: i.alt, width: 600, height: 600 })));
          stage.append(pair);
        }
        // Editorial exports may already contain their own badge; decorate only the generated pair.
        if (!editorial.length && !(this.config.image && !this.colorsChanged)) stage.append(element('span', 'MOMMY\n& ME', { class: 'mm-bundle__badge', 'aria-hidden': 'true' }));
      };
      showPair();
      const thumbs = element('div', null, { class: 'mm-bundle__thumbs' });
      const paired = element('button', null, { type: 'button', class: 'mm-bundle__thumb', 'aria-pressed': 'true', 'aria-label': 'View both pairs' });
      if (editorial.length || (this.config.image && !this.colorsChanged)) paired.append(element('img', null, { src: editorial[0]?.src || this.config.image, alt: '', width: 100, height: 120 }));
      else { const pair = element('span', null, { class: 'mm-bundle__thumb-pair' }); sources.filter(i => i.src).forEach(i => pair.append(element('img', null, { src: i.src, alt: '', width: 60, height: 120 }))); paired.append(pair); }
      const mark = button => [...thumbs.children].forEach(b => b.setAttribute('aria-pressed', String(b === button)));
      paired.addEventListener('click', () => { showPair(); mark(paired); });
      thumbs.append(paired);
      (editorial.length ? images.slice(1) : images).forEach((i, index) => {
        const button = element('button', null, { type: 'button', class: 'mm-bundle__thumb', 'aria-label': `View ${i.alt}, image ${index + 2}`, 'aria-pressed': 'false' });
        button.append(element('img', null, { src: i.src, alt: '', width: 80, height: 80, loading: 'lazy' }));
        button.addEventListener('click', () => { stage.replaceChildren(element('img', null, { class: 'mm-bundle__photo', src: i.src, alt: i.alt, width: 1000, height: 1000 })); mark(button); });
        thumbs.append(button);
      });
      const strip = element('div', null, { class: 'mm-bundle__gallery-nav' });
      const arrow = (label, direction) => { const button = element('button', direction < 0 ? '‹' : '›', { type: 'button', class: 'mm-bundle__gallery-arrow', 'aria-label': label }); button.addEventListener('click', () => { const items = [...thumbs.children]; const current = items.findIndex(b => b.getAttribute('aria-pressed') === 'true'); const next = items[(current + direction + items.length) % items.length]; next.click(); thumbs.scrollTo({ left: next.offsetLeft - thumbs.offsetLeft, behavior: 'smooth' }); }); return button; };
      strip.append(arrow('Previous bundle image', -1), thumbs, arrow('Next bundle image', 1));
      gallery.append(stage, strip);
    }

    async changeColors(candidates) {
      if (this.busy || this.uncertain) return;
      const changes = candidates.map((candidate, index) => ({ candidate, index })).filter(({ candidate, index }) => candidate && String(candidate.id) !== String(this.slots[index].product.id));
      if (!changes.length) return;
      this.busy = true;
      this.message(changes.length === 2 ? 'Updating both colors…' : 'Loading color…');
      this.render();
      try {
        // Fetch and validate ALL requested colors before changing either slot.
        const products = await Promise.all(changes.map(async ({ candidate, index }) => {
          const product = structuredClone(await this.colorProduct(candidate));
          if (kid(product) !== kid(this.slots[index].product)) throw new Error('This color belongs to a different product group.');
          product.selected_variant = product.variants.find(v => this.widget.variantAvailable(v)) || product.variants[0];
          product.selected_variant_id = product.selected_variant.id;
          product.status = 'ready';
          product.properties ||= {};
          product.reviews ||= {};
          return product;
        }));
        changes.forEach(({ index }, i) => {
          const slot = this.slots[index];
          slot.product = products[i];
          slot.values = products[i].options.map(o => o.values.length === 1 ? String(o.values[0]) : '');
          slot.chosen = false;
          this.widget.View.selectVariant(slot.product);
        });
        this.colorsChanged = this.slots.some((slot, i) => String(slot.product.id) !== this.initialIds[i]);
        this.sync();
        this.message(changes.length === 2 ? 'Both colors updated. Select a size for each pair.' : 'Color updated. Select a size for this pair.');
      } catch (error) { this.message(error.message); }
      finally { this.busy = false; if (this.isConnected) this.render(); }
    }

    async change(event) {
      const target = event.target;
      if (!target.matches('select[data-slot]') || this.busy || this.uncertain) return;
      const slot = this.slots[Number(target.dataset.slot)];
      if (target.hasAttribute('data-color')) {
        const candidate = slot.family.find(p => String(p.id) === target.value);
        if (!candidate || String(candidate.id) === String(slot.product.id)) return;
        const candidates = this.slots.map(() => null);
        candidates[Number(target.dataset.slot)] = candidate;
        await this.changeColors(candidates);
      } else {
        slot.values[Number(target.dataset.option)] = target.value;
        const variant = slot.values.every(Boolean) ? variantWithOptions(slot.product, slot.values) : null;
        slot.chosen = !!variant && this.widget.variantAvailable(variant);
        if (slot.chosen) {
          slot.product.selected_variant_id = variant.id;
          this.widget.View.selectVariant(slot.product);
          slot.product.selected_variant = variant;
        }
        this.message(this.ready() ? 'Your bundle is ready.' : 'Select available options for both pairs.');
        this.render();
      }
    }

    async add() {
      if (this.busy || this.uncertain || !this.widget || this.slots?.length !== 2) return;
      if (!this.ready()) {
        this.validationAttempted = true;
        const issues = selectionIssues(this.slots, variant => this.widget.variantAvailable(variant));
        const pairs = [...new Set(issues.map(issue => issue.slotIndex === 0 ? "women's" : "girls'"))].join(' and ');
        this.message(`Please choose ${issues.every(issue => /size/i.test(issue.name)) ? 'an available size' : 'available options'} for the ${pairs} sandals.`);
        this.render();
        const first = this.querySelector('select[aria-invalid="true"]');
        if (first) {
          first.focus({ preventScroll: true });
          if (typeof window.theme?.scrollToRevealElement === 'function') window.theme.scrollToRevealElement(first);
          else first.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
        return;
      }
      this.busy = true;
      this.message('Checking availability…');
      this.render();
      let submitted = false;
      try {
        // Shopify is authoritative for stock. Never submit a stale or partial pair.
        for (const slot of this.slots) {
          const url = new URL(`${this.config.root}products/${slot.product.handle}.js`, location.origin);
          const current = await (await request(url, this.abort.signal)).json();
          const selected = current.variants.find(v => String(v.id) === String(slot.product.selected_variant_id));
          if (!selected?.available) throw new Error('A selected size is no longer available. Please reload and choose another size.');
          if (Math.round(Number(slot.product.selected_variant.price) * 100) !== selected.price) throw new Error('A product price has changed. Please reload before adding this bundle.');
        }
        const cartUrl = new URL(`${this.config.root}cart.js`, location.origin);
        const before = await (await request(cartUrl, this.abort.signal)).json();
        const ids = this.slots.map(s => s.product.selected_variant_id);
        this.sync();
        this.message('Adding both pairs…');
        submitted = true;
        const result = addWithoutRedirect(this.widget);
        if (result?.then) await result;
        // Do not assume an undefined return value means success: verify both cart lines.
        let confirmed = false;
        for (let attempt = 0; attempt < 10 && this.isConnected; attempt += 1) {
          await new Promise(resolve => setTimeout(resolve, 800));
          const after = await (await request(cartUrl, this.abort.signal)).json();
          if (cartContainsAddition(before, after, ids)) { confirmed = true; break; }
        }
        if (!confirmed) throw new Error('We could not confirm both items were added. Check your bag before trying again.');
        this.message('Both pairs have been added to your bag.');
        this.querySelector('[data-cart-link]').hidden = false;
        openBundleCart(document, this.addButton);
      } catch (error) {
        this.message(error.message);
        // An uncertain result must not enable an accidental duplicate submission.
        if (submitted) { this.uncertain = true; this.querySelector('[data-cart-link]').hidden = false; }
      } finally {
        this.busy = false;
        if (this.isConnected) this.render();
      }
    }
  }
  customElements.define('mommy-me-bundle', MommyMeBundle);
})();
